import React, { useEffect, useState } from 'react';
import { tokenStorage } from '../../utils/auth';
import { mediaService } from '../../services/mediaService';
import {
  MediaLoadError,
  MediaLoadErrorKind,
} from '../../utils/mediaLoadError';
import { markMediaMissing } from '../../utils/missingMedia';

interface AuthImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  mediaId: string;
  fallback?: React.ReactNode;
}


// Authenticated image: fetches the media blob with Authorization header and renders via object URL.
// Loads once per mediaId, never retried. An image that will not load is not
// drawn at all — pass `fallback` (initials, say) for the places where
// something has to stand in its place.
export const AuthImage: React.FC<AuthImageProps> = ({ mediaId, alt, fallback, ...imgProps }) => {
  const [url, setUrl] = useState<string>('');
  const [error, setError] = useState<MediaLoadErrorKind | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    const controller = new AbortController();

    const load = async () => {
      setError(null);
      setUrl('');
      try {
        const token = tokenStorage.get();
        const res = await fetch(mediaService.getViewUrl(mediaId), {
          method: 'GET',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new MediaLoadError(res.status);
        }
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!revoked) setUrl(objectUrl);
      } catch (e: any) {
        if (!revoked && e.name !== 'AbortError') {
          const kind = e instanceof MediaLoadError ? e.kind : 'failed';
          // A file the server says is not there is gone for this session, and
          // every list that points at it can stop offering it
          if (kind === 'missing') markMediaMissing(mediaId);
          setError(kind);
        }
      }
    };

    load();

    return () => {
      revoked = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [mediaId]);

  // Nothing is shown for an image that will not load: a broken tile tells a
  // reader less than the absence of one, and where a face belongs the caller
  // passes initials to stand in for it
  if (error) return fallback ? <>{fallback}</> : null;

  if (!url) {
    return (
      <div
        {...(imgProps as any)}
        className={`animate-pulse bg-neutral-100 ${imgProps.className ?? ''}`}
        aria-label="loading"
      />
    );
  }

  return <img src={url} alt={alt} {...imgProps} />;
};
